// ── Adapter Supabase do IDashboardRepository ──────────────────────
// Implementa a porta do módulo Dashboard usando o cliente Supabase. É a
// ÚNICA camada que conhece as tabelas (cards, pipeline_stages, activities,
// leads, commissions, times, time_members, history) e aplica o filtro de
// escopo por usuário (applyUserFilter). As agregações ficam no service.

import { getSupabase, type TypedSupabaseClient } from '@infra/supabase/client';
import { applyUserFilter, type UserFilterContext } from '@utils';
import type { WorkspaceOriginsConfig } from '@/types/domain';
import type {
  IDashboardRepository,
  PipelineIdsParams,
  RangeQuery,
  FunnelStageRow,
  FunnelCardRow,
  TeamMemberRow,
  TeamCommRow,
  ActStatRow,
  AcqLeadRow,
  EffContactLeadRow,
  StageChangedHistoryRow,
  LossCardRow,
  ForecastStageRow,
  ForecastCardRow,
  TrackingLeadRow,
  EnterpriseFilter,
  EnterpriseTeamRow,
  EnterpriseSaleRow,
  EnterpriseLeadStageCardRow,
} from '@modules/dashboard';

const requireSupabase = (): TypedSupabaseClient => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase não inicializado.');
  return s;
};

export class SupabaseDashboardRepository implements IDashboardRepository {
  // Deps de outros domínios (config/pipelines) injetadas no core.ts — evita
  // importar @app ou a camada de queries aqui (ciclo com o composition root).
  constructor(
    private readonly fetchWorkspaceOrigins: (workspaceId: string) => Promise<WorkspaceOriginsConfig>,
    private readonly resolvePipelineIds: (params: PipelineIdsParams) => Promise<string[]>,
  ) {}

  getPipelineIds = (params: PipelineIdsParams): Promise<string[]> => {
    return this.resolvePipelineIds(params);
  };

  // 1) Funnel
  getFunnelStages = async (pipelineIds: string[]): Promise<FunnelStageRow[]> => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('pipeline_stages')
      .select('id, name, color, order_index, win_probability, pipeline_id, pipeline:pipelines!pipeline_stages_pipeline_id_fkey ( id, name, role )')
      .in('pipeline_id', pipelineIds)
      .eq('ativo', true)
      .order('order_index', { ascending: true });
    if (error) throw error;
    return (data || []) as unknown as FunnelStageRow[];
  };

  getFunnelCards = async ({ pipelineIds, startISO, endISO, userCtx }: { pipelineIds: string[] } & RangeQuery): Promise<FunnelCardRow[]> => {
    const supabase = requireSupabase();
    let cardsQ = supabase
      .from('cards')
      .select('id, stage_id, sdr_id, closer_id, status, faturamento, ativo, created_at')
      .in('pipeline_id', pipelineIds)
      .eq('ativo', true);
    if (startISO) cardsQ = cardsQ.gte('created_at', startISO);
    if (endISO)   cardsQ = cardsQ.lt('created_at', endISO);
    cardsQ = applyUserFilter(cardsQ, 'cards', userCtx);
    const { data, error } = await cardsQ;
    if (error) throw error;
    return (data || []) as unknown as FunnelCardRow[];
  };

  // 2) Team performance
  getActiveTimeIds = async (workspaceId: string): Promise<string[]> => {
    const supabase = requireSupabase();
    const { data: times } = await supabase
      .from('times').select('id').eq('workspace_id', workspaceId).eq('ativo', true);
    return (times || []).map(t => t.id);
  };

  getTeamMembers = async ({ timeIds, userCtx }: { timeIds: string[]; userCtx?: UserFilterContext | null }): Promise<TeamMemberRow[]> => {
    const supabase = requireSupabase();
    let usersQ = supabase
      .from('time_members')
      .select(`
        user_id, role,
        profile:profiles!time_members_user_id_fkey ( id, nome, initials, color, avatar_url, ativo )
      `)
      .in('time_id', timeIds)
      .eq('ativo', true);
    if (!userCtx?.canViewAll && userCtx?.id) {
      usersQ = usersQ.eq('user_id', userCtx.id);
    }
    const { data, error } = await usersQ;
    if (error) throw error;
    return (data || []) as unknown as TeamMemberRow[];
  };

  getLeadsSdrIds = async ({ workspaceId, startISO, endISO }: { workspaceId: string; startISO: string; endISO: string }): Promise<Array<{ sdr_id: string | null }>> => {
    const supabase = requireSupabase();
    const { data } = await supabase
      .from('leads')
      .select('sdr_id')
      .eq('workspace_id', workspaceId)
      .eq('ativo', true)
      .gte('created_at', startISO)
      .lt('created_at', endISO);
    return (data || []) as Array<{ sdr_id: string | null }>;
  };

  getCommissions = async ({ workspaceId, startDate, endDate }: { workspaceId: string; startDate: string; endDate: string }): Promise<TeamCommRow[]> => {
    const supabase = requireSupabase();
    const { data } = await supabase
      .from('commissions')
      .select('closer_id, sdr_id, closer_as_sdr, faturamento, sdr_rate, closer_commission, sdr_commission, card:cards ( closer_id, sdr_id )')
      .eq('workspace_id', workspaceId)
      .gte('won_date', startDate)
      .lt('won_date',  endDate);
    return (data || []) as unknown as TeamCommRow[];
  };

  // 3) Activities
  getActivities = async ({ workspaceId, startISO, endISO, userCtx }: { workspaceId: string } & RangeQuery): Promise<ActStatRow[]> => {
    const supabase = requireSupabase();
    let baseQ = supabase
      .from('activities')
      .select('id, status, type, due_date, lead_id, assigned_to, created_at, lead:leads ( id, nome )')
      .eq('workspace_id', workspaceId);
    if (startISO) baseQ = baseQ.gte('due_date', startISO);
    if (endISO)   baseQ = baseQ.lt('due_date',  endISO);
    baseQ = applyUserFilter(baseQ, 'activities', userCtx);
    const { data, error } = await baseQ;
    if (error) throw error;
    return (data || []) as unknown as ActStatRow[];
  };

  getDoneTodayCount = async ({ workspaceId, startOfTodayISO, userCtx }: { workspaceId: string; startOfTodayISO: string; userCtx?: UserFilterContext | null }): Promise<number> => {
    const supabase = requireSupabase();
    let doneTodayQ = supabase
      .from('activities')
      .select('id', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
      .eq('status', 'done')
      .gte('updated_at', startOfTodayISO);
    doneTodayQ = applyUserFilter(doneTodayQ, 'activities', userCtx);
    const { count } = await doneTodayQ;
    return count || 0;
  };

  // 4) Lead acquisition
  getAcquisitionLeads = async ({ workspaceId, startISO, endISO, userCtx }: { workspaceId: string } & RangeQuery): Promise<AcqLeadRow[]> => {
    const supabase = requireSupabase();
    let q = supabase
      .from('leads')
      .select('id, canal, sub_origem, campanha, origem')
      .eq('workspace_id', workspaceId)
      .eq('ativo', true);
    if (startISO) q = q.gte('created_at', startISO);
    if (endISO)   q = q.lt('created_at',  endISO);
    q = applyUserFilter(q, 'leads', userCtx);
    const { data, error } = await q;
    if (error) throw error;
    return (data || []) as unknown as AcqLeadRow[];
  };

  getWorkspaceOrigins = (workspaceId: string): Promise<WorkspaceOriginsConfig> => {
    return this.fetchWorkspaceOrigins(workspaceId);
  };

  // 5) Effective contact
  getEffectiveContactLeads = async ({ workspaceId, startISO, endISO, userCtx }: { workspaceId: string } & RangeQuery): Promise<EffContactLeadRow[]> => {
    const supabase = requireSupabase();
    let leadsQ = supabase
      .from('leads')
      .select('id, origem, canal')
      .eq('workspace_id', workspaceId)
      .eq('ativo', true);
    if (startISO) leadsQ = leadsQ.gte('created_at', startISO);
    if (endISO)   leadsQ = leadsQ.lt('created_at',  endISO);
    leadsQ = applyUserFilter(leadsQ, 'leads', userCtx);
    const { data } = await leadsQ;
    return (data || []) as unknown as EffContactLeadRow[];
  };

  getEffectiveContactStageIds = async (workspaceId: string): Promise<string[]> => {
    const supabase = requireSupabase();
    const { data } = await supabase
      .from('pipeline_stages')
      .select('id, pipelines!inner(workspace_id)')
      .eq('stage_role', 'em_contato')
      .eq('pipelines.workspace_id', workspaceId);
    return ((data || []) as unknown as Array<{ id: string }>).map(s => s.id);
  };

  getStageChangedHistory = async ({ workspaceId, startISO, endISO }: { workspaceId: string; startISO?: string | null; endISO?: string | null }): Promise<StageChangedHistoryRow[]> => {
    const supabase = requireSupabase();
    let historyQ = supabase
      .from('history')
      .select('lead_id, type, data')
      .eq('workspace_id', workspaceId)
      .eq('type', 'stage_changed');
    if (startISO) historyQ = historyQ.gte('created_at', startISO);
    if (endISO)   historyQ = historyQ.lt('created_at',  endISO);
    const { data } = await historyQ;
    return (data || []) as unknown as StageChangedHistoryRow[];
  };

  // 6) Loss reasons
  getLossCards = async ({ pipelineIds, startISO, endISO, userCtx }: { pipelineIds: string[] } & RangeQuery): Promise<LossCardRow[]> => {
    const supabase = requireSupabase();
    let q = supabase
      .from('cards')
      .select(`
        id, loss_reason, lost_at, pipeline_id,
        pipeline:pipelines ( id, name, role ),
        lead:leads!inner ( id, ativo )
      `)
      .in('pipeline_id', pipelineIds)
      .eq('status', 'lost')
      .eq('lead.ativo', true)
      .not('loss_reason', 'is', null);
    if (startISO)   q = q.gte('lost_at', startISO);
    if (endISO)     q = q.lt('lost_at', endISO);
    q = applyUserFilter(q, 'cards', userCtx);
    const { data, error } = await q;
    if (error) throw error;
    return (data || []) as unknown as LossCardRow[];
  };

  // 7) Forecast
  getForecastStages = async (pipelineIds: string[]): Promise<ForecastStageRow[]> => {
    const supabase = requireSupabase();
    const { data } = await supabase
      .from('pipeline_stages')
      .select('id, name, color, order_index, win_probability, pipeline_id')
      .in('pipeline_id', pipelineIds)
      .eq('ativo', true)
      .order('order_index', { ascending: true });
    return (data || []) as unknown as ForecastStageRow[];
  };

  getForecastCards = async ({ pipelineIds, userCtx }: { pipelineIds: string[]; userCtx?: UserFilterContext | null }): Promise<ForecastCardRow[]> => {
    const supabase = requireSupabase();
    let cardsQ = supabase
      .from('cards')
      .select('id, stage_id, faturamento, sdr_id, closer_id')
      .in('pipeline_id', pipelineIds)
      .eq('ativo', true)
      .eq('status', 'active');
    cardsQ = applyUserFilter(cardsQ, 'cards', userCtx);
    const { data } = await cardsQ;
    return (data || []) as unknown as ForecastCardRow[];
  };

  // 8) Tracking
  getTrackingLeads = async ({ workspaceId, startISO, endISO, userCtx }: { workspaceId: string } & RangeQuery): Promise<TrackingLeadRow[]> => {
    const supabase = requireSupabase();
    let leadsQ = supabase
      .from('leads')
      .select(`
        id, canal, sub_origem, campanha,
        utm_source, utm_medium, utm_campaign, utm_content,
        cards!cards_lead_id_fkey ( id, status, faturamento, won_at )
      `)
      .eq('workspace_id', workspaceId)
      .eq('ativo', true);
    if (startISO) leadsQ = leadsQ.gte('created_at', startISO);
    if (endISO)   leadsQ = leadsQ.lt('created_at',  endISO);
    leadsQ = applyUserFilter(leadsQ, 'leads', userCtx);
    const { data, error } = await leadsQ;
    if (error) throw error;
    return (data || []) as unknown as TrackingLeadRow[];
  };

  // Restringe cards/vendas a um time: mantém apenas linhas cujo sdr_id ou
  // closer_id esteja na lista de membros. `memberIds` vazio/nulo → sem filtro.
  private filterByMembers = <T extends { card?: { sdr_id: string | null; closer_id: string | null } | null; sdr_id?: string | null; closer_id?: string | null }>(
    rows: T[],
    memberIds: string[] | null | undefined,
  ): T[] => {
    if (!memberIds || memberIds.length === 0) return rows;
    const set = new Set(memberIds);
    return rows.filter((row) => {
      const sdr = row.card?.sdr_id ?? row.sdr_id ?? null;
      const closer = row.card?.closer_id ?? row.closer_id ?? null;
      return (sdr && set.has(sdr)) || (closer && set.has(closer));
    });
  };

  getEnterpriseTeams = async (workspaceId: string): Promise<EnterpriseTeamRow[]> => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('times')
      .select(`
        id, name,
        time_members!time_members_time_id_fkey ( user_id, ativo )
      `)
      .eq('workspace_id', workspaceId)
      .eq('ativo', true)
      .order('name', { ascending: true });
    if (error) throw error;
    return ((data || []) as unknown as Array<{ id: string; name: string; time_members?: Array<{ user_id: string | null; ativo: boolean | null }> | null }>)
      .map((t) => ({
        id: t.id,
        name: t.name,
        memberIds: (t.time_members || [])
          .filter(m => m.ativo && m.user_id)
          .map(m => m.user_id as string),
      }));
  };

  getEnterpriseSales = async ({ workspaceId, startISO, endISO, userCtx, memberIds }: { workspaceId: string } & EnterpriseFilter): Promise<EnterpriseSaleRow[]> => {
    const supabase = requireSupabase();
    let q = supabase
      .from('sales')
      .select(`
        id, lead_id, valor_total, forma_pagamento, data_venda,
        status_pagamento, valor_entrada, valor_restante, data_pagamento_restante,
        card:cards!sales_card_id_fkey (
          sdr_id, closer_id, stage_id, status,
          stage:pipeline_stages!cards_stage_id_fkey ( id, name )
        ),
        lead:leads!sales_lead_id_fkey!inner ( id, nome, origem, canal, proposta, ativo ),
        sale_plans ( quantity, total_value, kind_snapshot )
      `)
      .eq('workspace_id', workspaceId)
      .eq('lead.ativo', true);

    if (startISO) q = q.gte('data_venda', String(startISO).slice(0, 10));
    if (endISO)   q = q.lt('data_venda', String(endISO).slice(0, 10));

    const { data, error } = await q;
    if (error) throw error;

    let rows = (data || []) as unknown as EnterpriseSaleRow[];
    if (!userCtx?.canViewAll && userCtx?.id) {
      rows = rows.filter(row => row.card?.sdr_id === userCtx.id || row.card?.closer_id === userCtx.id);
    }
    return this.filterByMembers(rows, memberIds);
  };

  // Leitura cruzada do grupo enterprise. Vai por RPC porque `sales_select`
  // (Config/49) só libera o workspace do próprio usuário — um colaborador do
  // `enterprise` não lê uma linha de `aex` nem pedindo. A RPC é SECURITY
  // DEFINER e confere a associação do chamador; ver a migration
  // 20260903120000_enterprise_group_active_clients.sql.
  getEnterpriseGroupSales = async ({ workspaceId, startISO, endISO }: { workspaceId: string; startISO?: string | null; endISO?: string | null }): Promise<EnterpriseSaleRow[]> => {
    if (!workspaceId) return [];
    const supabase = requireSupabase();
    const { data, error } = await supabase.rpc('enterprise_group_active_clients', {
      p_workspace_id: workspaceId,
      // Omitido = sem recorte na ponta (a RPC trata NULL como "sem limite").
      p_start: startISO ? String(startISO).slice(0, 10) : undefined,
      p_end: endISO ? String(endISO).slice(0, 10) : undefined,
    });
    if (error) throw error;
    // A RPC já devolve card/lead/sale_plans no mesmo formato dos embeds que a
    // tela consumia antes, então não há segundo mapeamento aqui.
    return (data || []) as unknown as EnterpriseSaleRow[];
  };

  getEnterpriseLeadStageCards = async ({ pipelineIds, startISO, endISO, userCtx, memberIds }: { pipelineIds: string[] } & EnterpriseFilter): Promise<EnterpriseLeadStageCardRow[]> => {
    if (pipelineIds.length === 0) return [];
    const supabase = requireSupabase();
    let q = supabase
      .from('cards')
      .select(`
        id, lead_id, stage_id, status, faturamento, sdr_id, closer_id, created_at,
        stage:pipeline_stages!cards_stage_id_fkey ( id, name ),
        lead:leads!cards_lead_id_fkey!inner ( id, nome, status, proposta, ativo )
      `)
      .in('pipeline_id', pipelineIds)
      .eq('ativo', true)
      .eq('lead.ativo', true);

    if (startISO) q = q.gte('created_at', startISO);
    if (endISO)   q = q.lt('created_at', endISO);

    q = applyUserFilter(q, 'cards', userCtx);
    const { data, error } = await q;
    if (error) throw error;
    return this.filterByMembers((data || []) as unknown as EnterpriseLeadStageCardRow[], memberIds);
  };

  getEnterpriseNegotiationCards = async ({ pipelineIds, userCtx, memberIds }: { pipelineIds: string[]; userCtx?: UserFilterContext | null; memberIds?: string[] | null }): Promise<EnterpriseLeadStageCardRow[]> => {
    if (pipelineIds.length === 0) return [];
    const supabase = requireSupabase();
    // Snapshot: cards abertos AGORA (status='active'), sem recorte por data.
    let q = supabase
      .from('cards')
      .select(`
        id, lead_id, stage_id, status, faturamento, sdr_id, closer_id, created_at,
        stage:pipeline_stages!cards_stage_id_fkey ( id, name ),
        lead:leads!cards_lead_id_fkey!inner ( id, nome, status, proposta, ativo )
      `)
      .in('pipeline_id', pipelineIds)
      .eq('ativo', true)
      .eq('status', 'active')
      .eq('lead.ativo', true);

    q = applyUserFilter(q, 'cards', userCtx);
    const { data, error } = await q;
    if (error) throw error;
    return this.filterByMembers((data || []) as unknown as EnterpriseLeadStageCardRow[], memberIds);
  };
}
